/**
 * Teacher Assignment Detail - View assignment with attachments
 * Shows full assignment details with download options
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  ArrowLeft, 
  Edit,
  FileText,
  Download,
  Calendar,
  Clock,
  AlertCircle
} from 'lucide-react';

interface AssignmentDetail {
  id: string;
  classroom_name: string;
  subject_name: string;
  title: string;
  description?: string;
  due_at?: number;
  status: 'draft' | 'published' | 'closed';
  created_at: number;
  attachments: Array<{
    id: string;
    file_name: string;
    size_bytes: number;
    created_at: number;
  }>;
}

export function TeacherAssignmentDetail() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [assignment, setAssignment] = useState<AssignmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadAssignment();
    }
  }, [id]);

  const loadAssignment = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await apiService.getAssignment(id);
      setAssignment(response.data);
    } catch (err) {
      console.error('Failed to load assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      setDownloadingId(attachmentId);
      const blob = await apiService.downloadAssignmentAttachment(attachmentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      alert('Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return { bg: '#dcfce7', color: '#166534', label: 'Published' };
      case 'draft':
        return { bg: '#fef3c7', color: '#92400e', label: 'Draft' };
      case 'closed':
        return { bg: '#e2e8f0', color: '#475569', label: 'Closed' };
      default:
        return { bg: '#e2e8f0', color: '#475569', label: status };
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'No due date';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isOverdue = (dueAt?: number) => {
    if (!dueAt) return false;
    return dueAt < Date.now();
  };

  if (!user) return null;

  return (
    <Layout
      schoolName={'SMS'}
      principalName={user.loginId || 'Teacher'}
      onLogout={logout}
      role="teacher"
    >
      <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/teacher/assignments')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: 'none',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#64748b',
              marginBottom: '16px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Assignments
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: '24px' }}>
            <Card>
              <div style={{ padding: '24px', textAlign: 'center', color: '#dc2626' }}>
                <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
                <p>{error}</p>
                <Button size="small" variant="secondary" onClick={loadAssignment} style={{ marginTop: '16px' }}>
                  Retry
                </Button>
              </div>
            </Card>
          </div>
        )}

        {isLoading ? (
          <Card>
            <div style={{ padding: '32px' }}>
              <Skeleton height="400px" />
            </div>
          </Card>
        ) : assignment ? (
          <>
            {/* Assignment Details */}
            <div style={{ marginBottom: '24px' }}>
              <Card>
              <div style={{ padding: '32px' }}>
                {/* Header with Status and Edit Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#0f172a' }}>
                        {assignment.title}
                      </h1>
                      <span
                        style={{
                          padding: '6px 16px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: 500,
                          background: getStatusColor(assignment.status).bg,
                          color: getStatusColor(assignment.status).color
                        }}
                      >
                        {getStatusColor(assignment.status).label}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={18} />
                        <span><strong>Class:</strong> {assignment.classroom_name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={18} />
                        <span><strong>Subject:</strong> {assignment.subject_name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={18} />
                        <span style={{ color: isOverdue(assignment.due_at) && assignment.status === 'published' ? '#dc2626' : undefined }}>
                          <strong>Due:</strong> {formatDate(assignment.due_at)}
                          {isOverdue(assignment.due_at) && assignment.status === 'published' && ' (Overdue)'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={18} />
                        <span><strong>Created:</strong> {new Date(assignment.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {assignment.description && (
                      <div>
                        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                          Description
                        </h3>
                        <p style={{ 
                          fontSize: '14px', 
                          color: '#475569', 
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {assignment.description}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate(`/teacher/assignments/${assignment.id}/edit`)}
                    style={{ marginLeft: '16px' }}
                  >
                    <Edit size={16} style={{ marginRight: '8px' }} />
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
            </div>

            {/* Attachments */}
            {assignment.attachments && assignment.attachments.length > 0 && (
              <Card>
                <div style={{ padding: '32px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '16px' }}>
                    Attachments ({assignment.attachments.length})
                  </h2>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {assignment.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '16px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ 
                            padding: '12px', 
                            background: '#dbeafe', 
                            borderRadius: '8px'
                          }}>
                            <FileText size={24} style={{ color: '#2563eb' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 500 }}>
                              {attachment.file_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                              {formatFileSize(attachment.size_bytes)} • Uploaded {new Date(attachment.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        <Button
                          size="small"
                          onClick={() => handleDownload(attachment.id, attachment.file_name)}
                          disabled={downloadingId === attachment.id}
                        >
                          <Download size={16} style={{ marginRight: '8px' }} />
                          {downloadingId === attachment.id ? 'Downloading...' : 'Download'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
}
